import { cn } from '@/lib/utils';
import { Badge } from '@/components/ui/badge';
import { Phone, Gauge, Clock, Zap, MapPin, Route, BarChart3, AlertTriangle } from 'lucide-react';

interface ScoreItem {
  label: string;
  percent: number;
  detail: string;
  category?: string; // e.g. "now", "ongoing" for imminence
  icon: React.ReactNode;
}

interface ParsedReasoning {
  summary: string;
  isPastEvent: boolean;
  eventDate?: string;
  scores: ScoreItem[];
  touchpoints: string[];
  hubs: string[];
  callImpact?: string;
  finalScore?: number;
}

function parseReasoning(text: string): ParsedReasoning {
  const lines = text.split('\n');
  const result: ParsedReasoning = {
    summary: '',
    isPastEvent: false,
    scores: [],
    touchpoints: [],
    hubs: [],
  };

  const summaryLines: string[] = [];
  let hitStructured = false;

  for (const rawLine of lines) {
    const line = rawLine.trim();
    if (!line) continue;

    // Past event warning
    if (line.includes('PAST EVENT')) {
      result.isPastEvent = true;
      continue;
    }

    // Event date
    const dateMatch = line.match(/^\*\*Event Date:\*\*\s*(.+)/);
    if (dateMatch) {
      result.eventDate = dateMatch[1].trim();
      continue;
    }

    // Scoring Breakdown header
    if (line.match(/^\*\*Scoring Breakdown:?\*\*/)) {
      hitStructured = true;
      continue;
    }

    // Score line: "- Call Likelihood (85%): explanation"
    const scoreMatch = line.match(/^-\s+(.+?)\s*\((\d+)%(?:\s*-\s*(.+?))?\):\s*(.+)/);
    if (scoreMatch) {
      hitStructured = true;
      const [, label, pctStr, category, detail] = scoreMatch;
      const icons: Record<string, React.ReactNode> = {
        'Call Likelihood': <Phone className="w-3.5 h-3.5" />,
        'Scale of Impact': <Gauge className="w-3.5 h-3.5" />,
        'Imminence': <Clock className="w-3.5 h-3.5" />,
        'Actionability': <Zap className="w-3.5 h-3.5" />,
      };
      result.scores.push({
        label: label.trim(),
        percent: parseInt(pctStr, 10),
        detail: detail.trim(),
        category: category?.trim(),
        icon: icons[label.trim()] || <BarChart3 className="w-3.5 h-3.5" />,
      });
      continue;
    }

    // Touchpoints
    const tpMatch = line.match(/^\*\*Affected Customer Journey Touchpoints:\*\*\s*(.+)/);
    if (tpMatch) {
      hitStructured = true;
      result.touchpoints = tpMatch[1].split(',').map(s => s.trim()).filter(Boolean);
      continue;
    }

    // Hubs
    const hubMatch = line.match(/^\*\*Affected Hubs:\*\*\s*(.+)/);
    if (hubMatch) {
      hitStructured = true;
      result.hubs = hubMatch[1].split(',').map(s => s.trim()).filter(Boolean);
      continue;
    }

    // Call Impact
    const impactMatch = line.match(/^\*\*Estimated Call Impact:\*\*\s*(.+)/);
    if (impactMatch) {
      hitStructured = true;
      result.callImpact = impactMatch[1].trim();
      continue;
    }

    // Final Score
    const finalMatch = line.match(/^\*\*Final Relevance Score:\*\*\s*(\d+)%/);
    if (finalMatch) {
      hitStructured = true;
      result.finalScore = parseInt(finalMatch[1], 10);
      continue;
    }

    // Everything before structured data is summary
    if (!hitStructured) {
      summaryLines.push(line);
    }
  }

  result.summary = summaryLines.join(' ').replace(/\*\*(.+?)\*\*/g, '$1').trim();

  return result;
}

function scoreBarColor(percent: number): string {
  if (percent >= 80) return 'bg-red-500';
  if (percent >= 60) return 'bg-amber-500';
  if (percent >= 40) return 'bg-yellow-400';
  return 'bg-gray-400';
}

function scoreBarTrack(percent: number): string {
  if (percent >= 80) return 'bg-red-500/15';
  if (percent >= 60) return 'bg-amber-500/15';
  if (percent >= 40) return 'bg-yellow-400/15';
  return 'bg-gray-400/15';
}

function impactBadgeVariant(impact: string): string {
  const lower = impact.toLowerCase();
  if (lower.includes('significant') || lower.includes('major') || lower.includes('high') || lower.includes('critical'))
    return 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400 border-red-200 dark:border-red-800/40';
  if (lower.includes('moderate') || lower.includes('medium'))
    return 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400 border-amber-200 dark:border-amber-800/40';
  if (lower.includes('minor') || lower.includes('low') || lower.includes('minimal'))
    return 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400 border-green-200 dark:border-green-800/40';
  return 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400 border-blue-200 dark:border-blue-800/40';
}

function formatTouchpoint(tp: string): string {
  return tp
    .replace(/_/g, ' ')
    .replace(/\b\w/g, c => c.toUpperCase());
}

export function ReasoningDisplay({ reasoning }: { reasoning: string }) {
  if (!reasoning) {
    return <p className="text-sm text-muted-foreground italic">No reasoning provided.</p>;
  }

  const parsed = parseReasoning(reasoning);

  return (
    <div className="space-y-4">
      {/* Past Event Warning */}
      {parsed.isPastEvent && (
        <div className="flex items-center gap-2 px-3 py-2 rounded-lg bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800/40">
          <AlertTriangle className="w-4 h-4 text-amber-600 dark:text-amber-400 shrink-0" />
          <span className="text-sm font-medium text-amber-700 dark:text-amber-400">
            Past Event — This event has already occurred and is no longer actionable.
          </span>
        </div>
      )}

      {/* Summary */}
      {parsed.summary && (
        <p className="text-sm text-foreground/80 leading-relaxed">
          {parsed.summary}
        </p>
      )}

      {/* Event Date */}
      {parsed.eventDate && (
        <div className="flex items-center gap-2 text-sm">
          <Clock className="w-3.5 h-3.5 text-muted-foreground" />
          <span className="text-muted-foreground">Event Date:</span>
          <span className="font-medium text-foreground">{parsed.eventDate}</span>
        </div>
      )}

      {/* Scoring Breakdown */}
      {parsed.scores.length > 0 && (
        <div className="space-y-3">
          <h4 className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">
            Scoring Breakdown
          </h4>
          <div className="grid gap-2.5">
            {parsed.scores.map((score, i) => (
              <div key={i} className="group">
                <div className="flex items-center justify-between mb-1">
                  <div className="flex items-center gap-1.5 text-sm">
                    <span className={cn(
                      'flex items-center justify-center w-5 h-5 rounded',
                      scoreBarTrack(score.percent),
                    )}>
                      {score.icon}
                    </span>
                    <span className="font-medium text-foreground">{score.label}</span>
                    {score.category && (
                      <span className="text-xs text-muted-foreground">({score.category})</span>
                    )}
                  </div>
                  <span className={cn(
                    'text-sm font-bold tabular-nums',
                    score.percent >= 80 ? 'text-red-600 dark:text-red-400'
                      : score.percent >= 60 ? 'text-amber-600 dark:text-amber-400'
                        : score.percent >= 40 ? 'text-yellow-600 dark:text-yellow-400'
                          : 'text-muted-foreground'
                  )}>
                    {score.percent}%
                  </span>
                </div>
                {/* Bar */}
                <div className={cn('h-1.5 rounded-full overflow-hidden', scoreBarTrack(score.percent))}>
                  <div
                    className={cn('h-full rounded-full transition-all duration-500', scoreBarColor(score.percent))}
                    style={{ width: `${score.percent}%` }}
                  />
                </div>
                {/* Detail text */}
                <p className="text-xs text-muted-foreground mt-1 leading-relaxed">
                  {score.detail}
                </p>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Touchpoints & Hubs */}
      {(parsed.touchpoints.length > 0 || parsed.hubs.length > 0) && (
        <div className="flex flex-wrap gap-4">
          {parsed.touchpoints.length > 0 && (
            <div className="space-y-1.5 flex-1 min-w-[200px]">
              <h4 className="text-xs font-semibold text-muted-foreground uppercase tracking-wider flex items-center gap-1.5">
                <Route className="w-3.5 h-3.5" />
                Affected Touchpoints
              </h4>
              <div className="flex flex-wrap gap-1.5">
                {parsed.touchpoints.map(tp => (
                  <Badge key={tp} variant="secondary" className="text-[10px] font-normal">
                    {formatTouchpoint(tp)}
                  </Badge>
                ))}
              </div>
            </div>
          )}

          {parsed.hubs.length > 0 && (
            <div className="space-y-1.5">
              <h4 className="text-xs font-semibold text-muted-foreground uppercase tracking-wider flex items-center gap-1.5">
                <MapPin className="w-3.5 h-3.5" />
                Affected Hubs
              </h4>
              <div className="flex flex-wrap gap-1.5">
                {parsed.hubs.map(hub => (
                  <Badge key={hub} variant="outline" className="text-[10px] font-bold tracking-wider">
                    {hub}
                  </Badge>
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      {/* Call Impact */}
      {parsed.callImpact && (
        <div className="flex items-center gap-2">
          <span className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">Call Impact</span>
          <span className={cn(
            'inline-flex items-center px-2 py-0.5 rounded-md text-xs font-semibold border capitalize',
            impactBadgeVariant(parsed.callImpact)
          )}>
            {parsed.callImpact}
          </span>
        </div>
      )}
    </div>
  );
}

/**
 * Compact inline summary for card views - extracts just the key sentence.
 */
export function ReasoningSummary({ reasoning }: { reasoning: string }) {
  if (!reasoning) return null;

  const parsed = parseReasoning(reasoning);

  // Show summary + key metrics inline
  const topScore = parsed.scores.length > 0
    ? parsed.scores.reduce((a, b) => a.percent > b.percent ? a : b)
    : null;

  return (
    <span className="text-sm text-muted-foreground">
      {parsed.summary
        ? parsed.summary.length > 160 ? parsed.summary.slice(0, 157) + '...' : parsed.summary
        : 'No reasoning provided.'}
      {topScore && (
        <span className={cn(
          'ml-1.5 inline-flex items-center text-xs font-medium',
          topScore.percent >= 80 ? 'text-red-500' : topScore.percent >= 60 ? 'text-amber-500' : 'text-muted-foreground'
        )}>
          · {topScore.label} {topScore.percent}%
        </span>
      )}
    </span>
  );
}
